import React, { useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import {
  Box,
  CircularProgress,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Row } from 'components/Layout';

const StyledTableWrapper = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  fontSize: '12px',
  color: theme.palette.text.darkBodyText,
  '& td, & th': {
    textAlign: 'right',
    border: 'none',
    padding: '12px',
  },
  '& td:not(:first-of-type), & th:not(:first-of-type)': {
    width: '178px',
    paddingRight: theme.spacing(6),
    borderRight: `1px solid ${theme.palette.grey[200]}`,
  },
  '& td:first-of-type, & th:first-of-type': {
    position: 'sticky',
    minWidth: '300px',
    maxWidth: '300px',
    width: '300px',
    fontWeight: '600',
    textAlign: 'left',
    left: 0,
    zIndex: 2,
    padding: '12px',
    marginTop: '8px',
    backgroundColor: theme.palette.background.paper,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: '8px',
      background:
        'linear-gradient(to right, rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0))',
      pointerEvents: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '150px',
      maxWidth: '150px',
      width: '150px',
    },
  },
  '& table': {
    tableLayout: 'fixed',
    width: 'fit-content',
  },
}));

type Field<T = Record<string, any>> = {
  key: string;
  label: string | React.ReactNode;
  tooltip?: React.ReactNode;
  render?: (value: any, item: T) => React.ReactNode;
  isLoading?: boolean;
  hasBackground?: boolean;
  getWidth?: (item: T) => string | undefined;
};

type VerticalTableProps<T = Record<string, any>> = {
  fields: Array<Field<T>>;
  data: Array<T>;
  emptyMessage?: string;
  isLoading: boolean;
};

const VerticalTable = <T extends Record<string, any>>({
  fields,
  data,
  emptyMessage,
  isLoading,
}: VerticalTableProps<T>): React.ReactElement => {
  const tableRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // Calculate the width for each column by checking all fields
  const columnWidths = data.map((item) => {
    let customWidth: string | undefined = undefined;
    fields.forEach((field) => {
      if (field.getWidth) {
        const fieldWidth = field.getWidth(item);
        // If any field specifies a custom width for this column, use it
        if (fieldWidth) {
          customWidth = fieldWidth;
        }
      }
    });
    return customWidth;
  });

  // Helper function to check if content should be wrapped in Typography
  const shouldWrapInTypography = (content: React.ReactNode): boolean => {
    // Only wrap primitive values (strings, numbers) in Typography
    // React elements should not be wrapped to avoid DOM nesting issues
    return typeof content === 'string' || typeof content === 'number';
  };

  return (
    <StyledTableWrapper ref={tableRef}>
      {!isLoading && data.length === 0 && emptyMessage && (
        <Typography
          variant="body2"
          color="text.secondary"
          marginLeft={3}
          marginTop={2.5}
        >
          {emptyMessage}
        </Typography>
      )}

      <Table>
        <TableBody>
          {fields.map((field, index) => (
            <TableRow
              key={`${field.key}-${index}`}
              sx={{
                backgroundColor: field.hasBackground
                  ? theme.palette.grey[200]
                  : 'inherit',
              }}
            >
              <TableCell
                sx={{
                  backgroundColor: field.hasBackground
                    ? `${theme.palette.grey[200]} !important`
                    : 'inherit',
                  ...(data.length === 0 &&
                    index === 0 && {
                      '&::after': {
                        display: 'none',
                      },
                    }),
                }}
              >
                <Row spacing="xs">
                  {field.label}
                  {field.tooltip && (
                    <Tooltip title={field.tooltip} arrow>
                      <InfoOutlined fontSize="small" color="disabled" />
                    </Tooltip>
                  )}
                </Row>
              </TableCell>
              {isLoading ? (
                <TableCell>
                  {field.key === fields[0].key && (
                    <Typography variant="body2" color="text.secondary">
                      <CircularProgress />
                    </Typography>
                  )}
                </TableCell>
              ) : (
                data.map((item, index) => {
                  const content = field.render
                    ? field.render(item[field.key], item)
                    : item[field.key];

                  const columnWidth = columnWidths[index];

                  return (
                    <TableCell
                      key={`${field.key}-${index}`}
                      sx={{
                        borderRight: field.hasBackground
                          ? `1px solid ${theme.palette.grey[300]} !important`
                          : 'inherit',
                        ...(columnWidth
                          ? {
                              width: `${columnWidth} !important`,
                              minWidth: `${columnWidth} !important`,
                              maxWidth: `${columnWidth} !important`,
                            }
                          : {}),
                      }}
                    >
                      {shouldWrapInTypography(content) ? (
                        <Typography variant="body2" fontWeight="bold">
                          {content}
                        </Typography>
                      ) : (
                        content
                      )}
                    </TableCell>
                  );
                })
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableWrapper>
  );
};

export default VerticalTable;
